import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { apiClient, wsUrl } from "@/lib/api";
import { KonattuLogo } from "@/lib/Logo";
import { maskPhone, formatIST } from "@/lib/branches";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, Lock, LockOpen } from "lucide-react";

function formatDrawDate(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
    const dt = new Date(Date.UTC(y, m - 1, d));
    return dt.toLocaleDateString("en-IN", {
      weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

export default function WinnersPage() {
  const [winners, setWinners] = useState([]);
  const [latestDate, setLatestDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manageMode, setManageMode] = useState(false);
  const [pinPromptOpen, setPinPromptOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const wsRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/winners/latest");
      setWinners(data.winners || []);
      setLatestDate(data.latest_draw_date || null);
    } catch (_e) { /* noop */ }
    setLoading(false);
  };

  useEffect(() => {
    load();
    try {
      const ws = new WebSocket(wsUrl());
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (["winner_drawn","winner_deleted","reset_today","registrations_cleared"].includes(msg.type)) {
            load();
          }
        } catch (_e) { /* noop */ }
      };
      ws.onclose = () => {};
    } catch (_e) { /* noop */ }
    const poll = setInterval(load, 30000);
    return () => {
      clearInterval(poll);
      try { wsRef.current?.close(); } catch (_e) { /* noop */ }
    };
  }, []);

  const enableManageMode = () => {
    // If already authed in this session, skip PIN prompt
    if (sessionStorage.getItem("kg_admin") === "1") {
      setManageMode(true);
      return;
    }
    setPin("");
    setPinPromptOpen(true);
  };

  const submitPin = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/admin/login", { pin });
      sessionStorage.setItem("kg_admin", "1");
      setManageMode(true);
      setPinPromptOpen(false);
      setPin("");
      toast.success("Manage mode enabled");
    } catch {
      toast.error("Incorrect PIN");
    }
  };

  const confirmDelete = (w) => setDeleteTarget(w);

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/winners/${deleteTarget.id}`, { params: { pin: "1234" } });
      toast.success("Winner removed");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex flex-col items-center mb-10 relative">
          <KonattuLogo className="h-16" showTagline={false} />
          <h1 className="font-display text-4xl md:text-6xl text-neutral-900 mt-6 text-center">
            🏆 Lucky Draw Winners
          </h1>
          {latestDate ? (
            <p className="text-neutral-500 mt-2 text-sm md:text-base" data-testid="winners-draw-date">
              Draw of {formatDrawDate(latestDate)}
            </p>
          ) : (
            <p className="text-neutral-500 mt-2 text-sm md:text-base">
              Awaiting the next morning&apos;s draw…
            </p>
          )}

          {/* Manage toggle (admin only) */}
          <div className="absolute top-0 right-0">
            {manageMode ? (
              <Button
                variant="outline"
                size="sm"
                className="border-[#CC0000] text-[#CC0000] hover:bg-[#FFE9E9]"
                onClick={() => setManageMode(false)}
                data-testid="exit-manage-mode"
              >
                <LockOpen className="w-4 h-4 mr-1" /> Exit Manage
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="text-neutral-400 hover:text-[#CC0000]"
                onClick={enableManageMode}
                data-testid="enter-manage-mode"
                title="Admin: manage winners"
              >
                <Lock className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {loading ? (
          <p className="text-center text-neutral-500" data-testid="winners-loading">Loading…</p>
        ) : winners.length === 0 ? (
          <div className="text-center py-16">
            <p className="font-display text-2xl md:text-3xl text-neutral-700" data-testid="no-winners">
              No winners announced yet. Check back tomorrow morning! 🎰
            </p>
            <p className="text-neutral-500 mt-3">Today&apos;s bills are eligible for tomorrow morning&apos;s draw.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 tv-grid gap-6" data-testid="winners-grid">
            {winners.map((w) => (
              <div key={w.id} className="kg-winner-card p-6 fade-up relative" data-testid={`winner-card-${w.branch}`}>
                {manageMode && (
                  <button
                    type="button"
                    onClick={() => confirmDelete(w)}
                    className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white border-2 border-red-200 text-[#CC0000] hover:bg-[#CC0000] hover:text-white hover:border-[#CC0000] transition-colors flex items-center justify-center"
                    title="Delete this winner"
                    data-testid={`delete-winner-${w.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <p className="text-xs uppercase tracking-widest font-semibold" style={{color:"#CC0000"}}>{w.branch}</p>
                <h3 className="font-display text-3xl md:text-4xl text-neutral-900 mt-3 leading-tight">
                  {w.name}
                </h3>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-100">
                  <span className="text-neutral-600 font-mono">{maskPhone(w.phone)}</span>
                  <span className="text-xs text-neutral-500">Drawn {formatDrawDate(w.draw_date)} · {formatIST(w.drawn_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 text-center">
          <Link to="/" className="text-sm font-medium" style={{color:"#CC0000"}} data-testid="back-to-register-link">
            ← Back to Registration
          </Link>
        </div>
      </div>

      {/* PIN prompt for manage mode */}
      <AlertDialog open={pinPromptOpen} onOpenChange={setPinPromptOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Admin PIN</AlertDialogTitle>
            <AlertDialogDescription>
              Enter the admin PIN to enable delete controls for winners.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <form onSubmit={submitPin}>
            <Input
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))}
              placeholder="••••"
              autoFocus
              className="h-12 text-center text-xl tracking-[0.4em] border-2"
              data-testid="winners-pin-input"
            />
            <AlertDialogFooter className="mt-4">
              <AlertDialogCancel type="button" data-testid="winners-pin-cancel">Cancel</AlertDialogCancel>
              <Button type="submit" className="kg-btn-red" data-testid="winners-pin-submit">Unlock</Button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this winner?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <b>{deleteTarget?.name}</b> from <b>{deleteTarget?.branch}</b> ({formatDrawDate(deleteTarget?.draw_date)}). The branch will be able to draw again for that date.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-winner-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-[#CC0000] hover:bg-[#990000]" data-testid="delete-winner-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
